import React, { useState, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import Skeleton from '@material-ui/lab/Skeleton';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { Search } from 'assets/assetsV1/iconComponents';

import { PEER_NOMINATION_COHORTS } from 'constants/ProjectConstants';
import { getRandomInt } from 'constants/helper';
import { sendSearchedNominationsEvent } from 'constants/mixpanelHelper';

import { NominationPeerSearchByEmail } from 'services/search';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { Chip } from 'uiToolkitV1/Chip';
import { Divider } from 'uiToolkitV1/Divider';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextLabel } from 'uiToolkitV1/Typography';

const CircleSkeleton = styled(Skeleton)`
  display: inline-block;
`;

const RectSkeleton = styled(CircleSkeleton)`
  display: inline-block;
  vertical-align: super;
  margin-left: 1rem;
`;

const Container = styled.div`
  padding: 0 8px;
  width: 100%;
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
`;

const ValueContainer = ({ children, ...props }) => (
  <Container>
    <Search boxSize="18px" mr="6px" />
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  </Container>
);

const AvatarComponent = (data) => () => {
  if (!data.profilePhoto) return null;
  return <Avatar src={data.profilePhoto} />;
};

const NoOptionsMessage = (searchText) => (props) => (
  <components.NoOptionsMessage {...props}>
    {searchText ? 'No results found' : 'Type To Search'}
  </components.NoOptionsMessage>
);

function LoadingMessage(props) {
  return (
    <components.LoadingMessage {...props}>
      <>
        <Flex alignItems="center" justifyContent="flex-start" mb={2}>
          <CircleSkeleton variant="circle" width={34} height={34} />
          <RectSkeleton variant="rect" width={180} height={22} />
        </Flex>
        <Flex alignItems="center" justifyContent="flex-start">
          <CircleSkeleton variant="circle" width={34} height={34} />
          <RectSkeleton variant="rect" width={225} height={22} />
        </Flex>
      </>
    </components.LoadingMessage>
  );
}

const Option = (recommendedPeers) => (props) => {
  const { data } = props;
  const { companyId, companyName } = data || {};

  if (data.value === 'error') {
    return (
      <components.Option {...props}>
        <TextLabel>{data.label}</TextLabel>
      </components.Option>
    );
  }

  // eslint-disable-next-line eqeqeq
  const isRecommended = recommendedPeers.findIndex(({ employee }) => employee.id == data.id) !== -1;

  const displayEmail = calculateDisplayEmail(data);

  if (isRecommended) {
    return (
      <components.Option {...props}>
        <Flex gap="12px" justify="space-between" alignItems="center" maxW="100%">
          <Box p={3} maxW="50%" opacity={data.isDisabled ? '.5' : 1}>
            <ListItemComp
              title={data.displayName}
              subTitle={data.designation}
              subSubtitle={companyId && companyName ? [displayEmail, `${companyId}, ${companyName}`] : displayEmail}
              LeftComponent={AvatarComponent(data)}
              withoutBackground
              isBoldText={false}
            />
          </Box>

          <Box flexGrow={1}>
            <TagColored color="black">Recommended</TagColored>
          </Box>
          <Flex>
            {data.isDisabled && <TagColored color="black">Nominated</TagColored>}
            {!data.isDisabled && (
              <Button variant="tertiary" isDisabled={data.isDisabled}>
                Nominate
              </Button>
            )}
          </Flex>
        </Flex>
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <Flex gap="12px" justify="space-between" alignItems="center" maxW="100%">
        <Box p={3} maxW="80%" opacity={data.isDisabled ? '.5' : 1}>
          <ListItemComp
            title={data.name}
            subTitle={data.designation}
            subSubtitle={companyId && companyName ? `${displayEmail} (${companyId}, ${companyName})` : displayEmail}
            LeftComponent={AvatarComponent(data)}
            withoutBackground
            isBoldText
          />
        </Box>

        {isRecommended && (
          <Box flexGrow={1}>
            <TagColored color="black">Recommended</TagColored>
          </Box>
        )}
        <Flex>
          {data.isDisabled && <TagColored color="black">Nominated</TagColored>}
          {!data.isDisabled && (
            <Button variant="tertiary" isDisabled={data.isDisabled}>
              Nominate
            </Button>
          )}
        </Flex>
      </Flex>
    </components.Option>
  );
};

const RecommendedGroupLabel = ({ label }) => {
  if (label !== 'Recommended') {
    return null;
  }
  return (
    <>
      <Box px={2} py={1}>
        <Chip variant="outlined" selected>
          {label}
        </Chip>
      </Box>

      <Divider py={1} />
    </>
  );
};

const getDefaultOptionsFromRecommendedPeers = ({ recommendedPeers, cohortKey, exclusions = [] }) => {
  const filteredPeers = recommendedPeers.filter(({ peerPoolTypes }) => {
    if (cohortKey === PEER_NOMINATION_COHORTS.ALL_ORG) return true;
    return peerPoolTypes.includes(cohortKey);
  });
  const returnOptions = {
    label: 'Recommended',
    options: filteredPeers.map(({ employee }) => ({
      ...employee,
      value: employee.id,

      peerPoolType: cohortKey,
      isDisabled: exclusions.includes(employee.id),
    })),
  };
  return [returnOptions];
};

const StyledAsyncSelect = styled(AsyncSelect)(({ theme }) => ({
  display: 'block',
  '.react-select-nomination-search__control': {
    background: theme.colors.neutral.F5F7F9,
    minHeight: '36px',
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: '4px',
    transition: 'width 200ms',
    '& svg': {
      color: theme.colors.neutral['999999'],
    },
    '& p': {
      color: theme.colors.neutral['999999'],
    },
    '&:hover': {
      borderColor: 'transparent',
      '& svg': {
        color: theme.colors.neutral['666666'],
      },
      '& p': {
        color: theme.colors.neutral['666666'],
      },
    },
    '&--is-focused': {
      background: theme.colors.common.white,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
    '&--is-focused:hover': {
      background: theme.colors.common.white,
      boxShadow: theme.shadows.search,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
  },
  '.react-select-nomination-search__indicators': {
    display: 'none',
  },
  '.react-select-nomination-search__group ': {},
  '.react-select-nomination-search__value-container': {
    padding: '0',
  },
  '.react-select-nomination-search__input': {
    height: '16px',
  },
  '.react-select-nomination-search__input-container': {
    padding: 0,
    margin: 0,
    height: '16px',
  },
  '.react-select-nomination-search__option--is-focused': {
    backgroundColor: theme.colors.functional.E6F1FD,
    cursor: 'pointer',
  },
  '.react-select-nomination-search__option': {
    padding: '8px',
    backgroundColor: theme.colors.common.white,
    '&--is-selected': {
      backgroundColor: theme.colors.functional.E6F1FD,
    },
  },
  '.react-select-nomination-search__option:hover': {
    backgroundColor: theme.colors.functional.E6F1FD,
  },
}));

const SearchPeers = ({
  allNominated,
  exclusions,
  nominatePeer,
  placeholderText,
  cohortKey, // API requires a cohort key for searching employees like -6 for Seniors, -7 for Juniors, etc
  empId,
  reviewId,
  recommendedPeers,
}) => {
  const [searchText, setSearchText] = useState('');
  const [random, setRandom] = useState(getRandomInt(999999));
  useEffect(() => setRandom(getRandomInt(999999)), [allNominated]);
  const [isLoading, setIsLoading] = useState(false);

  const selectStyles = {
    option: (provided, { imageUrl }) => ({
      ...provided,
      cursor: 'pointer',
      padding: imageUrl ? '10px 14px' : '14px',
      fontWeight: 500,
      '&:hover': {
        background: 'rgba(230, 236, 240, 0.4)',
      },
    }),
    loadingMessage: (provided) => ({
      ...provided,
      padding: '1em 14px',
      textAlign: 'left',
    }),
  };

  const handleInputChange = (value) => setSearchText(value);

  const loadOptions =
    (empId_) =>
    // eslint-disable-next-line default-param-last
    (text = '', callback) => {
      sendSearchedNominationsEvent({
        eventProperties: {
          reviewCycleId: reviewId,
          empId: empId_ || 'NA',
          query: text,
        },
      });
      return NominationPeerSearchByEmail(
        {
          text,
          callback,
          tagType: 'EMPLOYEE_NAME_AND_EMAIL',
          limit: 15,
          cohort: [PEER_NOMINATION_COHORTS.ALL_ORG, PEER_NOMINATION_COHORTS.DLM].includes(cohortKey)
            ? 'ALL_EMPLOYEE'
            : cohortKey,
          empId: empId_,
        },
        [...allNominated.map((n) => n.peerProfile.employee.id), ...exclusions]
      );
    };

  const handleChange = (option) => {
    setIsLoading(true);

    // this is specifically for recommended employees fetched with the peer pool details because it has emp object with different key names
    const employee = option.employee ?? {
      id: Number(option.id),
      displayName: option.displayName,
      profilePhoto: option.profilePhoto,
    };

    nominatePeer({ employee, cohortKey }).finally(() => setIsLoading(false));
  };

  const defaultOptions = getDefaultOptionsFromRecommendedPeers({
    recommendedPeers,
    cohortKey,
    exclusions,
  });

  return (
    <StyledAsyncSelect
      placeholder={`${placeholderText}`}
      key={random}
      components={{
        LoadingMessage,
        NoOptionsMessage: NoOptionsMessage(searchText),
        Option: Option(recommendedPeers),
        ValueContainer,
      }}
      loadOptions={loadOptions(empId)}
      onInputChange={handleInputChange}
      classNamePrefix="react-select-nomination-search"
      onChange={handleChange}
      menuPlacement="auto"
      value="searchText"
      styles={selectStyles}
      defaultOptions={defaultOptions}
      formatGroupLabel={RecommendedGroupLabel}
      isDisabled={isLoading}
    />
  );
};

export default SearchPeers;
